import { useState, useCallback, useEffect, useMemo } from "react";
import { useLazyGetRecurringTransactionsQuery } from "../../../../services/recurringTransactions";
import { RecurringTransactionsResponse } from "../../../../services/recurringTransactions/types";
import { OrderType } from "../../../../services";
import { SortConfig, SortValueType } from "../../../Transactions/types";

export interface UseRecurringTransactionsProps {
  activeTab: string;
  accountId?: number;
}

export interface UseRecurringTransactionsReturn {
  recurringTransactions: RecurringTransactionsResponse[];
  sortedRecurringTransactions: RecurringTransactionsResponse[];
  isLoadingRecurring: boolean;
  recurringSortConfig: SortConfig;
  setRecurringSortConfig: (config: SortConfig) => void;
  refetch: () => Promise<void>;
  refetchRecurring: () => Promise<void>;
  handleRecurringSortChange: (field: any, order: any) => void;
}

/**
 * Custom hook to manage recurring transactions
 */
export const useRecurringTransactions = ({
  activeTab,
  accountId,
}: UseRecurringTransactionsProps): UseRecurringTransactionsReturn => {
  const [recurringSortConfig, setRecurringSortConfig] = useState<SortConfig>({
    field: SortValueType.TRANSACTION_DATE,
    order: OrderType.ASCENDING,
  });
  const [
    fetchRecurringTransactions,
    { data: recurringTransactions = [], isFetching: isLoadingRecurring },
  ] = useLazyGetRecurringTransactionsQuery();

  const refetchRecurring = useCallback(async () => {
    await fetchRecurringTransactions(accountId)
      .unwrap()
      .catch(() => undefined);
  }, [accountId, fetchRecurringTransactions]);

  useEffect(() => {
    if (activeTab === "recurring") {
      refetchRecurring();
    }
  }, [activeTab, refetchRecurring]);

  const handleRecurringSortChange = useCallback(
    (field: SortValueType, order: OrderType) => {
      setRecurringSortConfig({ field, order });
    },
    [],
  );

  // Sort recurring transactions in memory
  const sortedRecurringTransactions = useMemo(() => {
    const sorted = [...recurringTransactions];

    sorted.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (recurringSortConfig.field) {
        case SortValueType.AMOUNT:
          aValue = a.amount;
          bValue = b.amount;
          break;
        case SortValueType.TRANSACTION_DATE:
          aValue = new Date(a.nextExecutionDate).getTime();
          bValue = new Date(b.nextExecutionDate).getTime();
          break;
        case SortValueType.CATEGORY:
          aValue = a.categoryName || "";
          bValue = b.categoryName || "";
          break;
        default:
          return 0;
      }

      if (recurringSortConfig.order === OrderType.ASCENDING) {
        if (aValue > bValue) return 1;
        if (aValue < bValue) return -1;
        return 0;
      } else {
        if (aValue < bValue) return 1;
        if (aValue > bValue) return -1;
        return 0;
      }
    });

    return sorted;
  }, [recurringTransactions, recurringSortConfig]);

  return {
    recurringTransactions,
    sortedRecurringTransactions,
    isLoadingRecurring,
    recurringSortConfig,
    setRecurringSortConfig,
    refetch: refetchRecurring,
    refetchRecurring,
    handleRecurringSortChange,
  };
};
